import React from "react";
import { createPortal } from "react-dom";
import ConfirmDialog from "./confirmDialog";
import { activateDialog } from "./dialogFocus";
import DateUtility from "./dateUtility";
import FetchHelper from "./fetchHelper";
import { SWAP_CHANGED_EVENT } from "./swapShiftLine";
/**
 * Taking a shift from where it is listed: a byttevagt another volunteer has put on the exchange
 * (Overtag) or an open place on a yellow shift (Tag vagten). Both are the same thing to the server,
 * since an open place has a byttevagt of its own, so both are one question and one request to
 * `shiftexchange/acquire` - without the detour over the page of the byttevagt.
 *
 * The question is answered in green, since yes is the positive answer. What the server says
 * afterwards - taken, or refused in Danish (the volunteer has another shift then, someone was
 * first) - is told in a box the volunteer closes, so a refusal is never lost. Every list that shows
 * shifts re-fetches when the answer is yes, through the event the swap flows already use.
 */
/** The words the question is asked in, for a byttevagt as the lists carry it. */
export const overtagQuestion = (when, from) => `Vil du overtage vagten ${when} fra ${from}?`;
/** The words the question is asked in, for an open place on a yellow shift. */
export const tagVagtenQuestion = (when, role) => `Vil du tage vagten ${when} som ${role === "Operator" ? "operatør" : "kiosk"}?`;
/** When a shift starts, in the words of a sentence: "fredag 6. november kl. 17:30". */
export const whenText = (dtStart) => {
    const start = DateUtility.parse(dtStart);
    const day = DateUtility.longDay(start);
    return `${day.charAt(0).toLowerCase()}${day.slice(1)} kl. ${start.toFormat("HH:mm")}`;
};
/** A message the volunteer closes: what happened to the shift they took, or why they could not. */
export const NoticeDialog = ({ message, heading, onClose }) => {
    const dialogRef = React.useRef(null);
    // Who had the focus before the "Ok" button took it (autoFocus).
    const opener = React.useRef(document.activeElement);
    // Escape closes it, Tab stays in it, and the focus goes back to what opened it.
    React.useEffect(() => activateDialog(() => dialogRef.current, onClose, opener.current), []);
    return createPortal(React.createElement("div", { className: "bp-dialog-backdrop", onClick: onClose },
        React.createElement("div", { className: "bp-dialog", ref: dialogRef, tabIndex: -1, role: "alertdialog", "aria-modal": "true", "aria-label": heading ? `${heading}. ${message}` : message, onClick: (e) => e.stopPropagation() },
            heading ? React.createElement("h2", { className: "bp-dialog-heading" }, heading) : null,
            React.createElement("p", null, message),
            React.createElement("div", { className: "bp-dialog-actions" },
                React.createElement("button", { type: "button", className: "btn btn-outline-secondary", autoFocus: true, onClick: onClose }, "Ok")))), document.body);
};
export default class AcquireShift extends React.Component {
    constructor(props) {
        super(props);
        this.acquire = () => {
            this.setState({ busy: true });
            FetchHelper.post("/bpapi/shiftexchange/acquire", { slug: this.props.slug })
                .then((result) => {
                if (!result.success) {
                    this.setState({ busy: false, notice: result.errorMessage || "Vagten kunne ikke overtages." });
                    return;
                }
                const when = DateUtility.parse(result.shiftStartTime).toFormat("dd-MM-yyyy 'kl.' HH:mm");
                const text = this.props.yellow || !result.substitutedMemberName
                    ? `Du har taget vagten d. ${when}.`
                    : `Du har overtaget vagten d. ${when} fra ${result.substitutedMemberName}.`;
                this.setState({ busy: false, notice: text });
                window.dispatchEvent(new CustomEvent(SWAP_CHANGED_EVENT));
            })
                .catch(() => this.setState({ busy: false, notice: "Vagten kunne ikke overtages. Prøv igen." }));
        };
        /** An open place on a yellow shift is taken, a byttevagt is taken over. */
        this.heading = () => this.props.yellow ? "Tag vagten" : "Overtag vagt";
        this.state = { busy: false, notice: null };
    }
    render() {
        if (this.state.notice) {
            return React.createElement(NoticeDialog, { heading: this.heading(), message: this.state.notice, onClose: this.props.onClose });
        }
        return React.createElement(ConfirmDialog, { heading: this.heading(), message: this.props.question, yesLabel: this.props.yesLabel, positive: true, busy: this.state.busy, onYes: this.acquire, onNo: this.props.onClose });
    }
}
